import { faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BarChart2, Edit, Plus, Trash2 } from 'lucide-react';
import * as React from 'react';
import { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingComponent } from 'src/components/shared/LoadingComponent';
import { ProgressBar } from 'src/components/shared/ProgressBar';
import { AppAgentContext } from 'src/context/ContextProvider';
import useIgnoreRenderEffect from 'src/hooks/useIgnoreRenderEffect';
import { getAllDocumentsService, getLastResultsDocumentsService } from 'src/services/DocumentService';
import { appActions, DataStates, State } from 'src/store';
import { ModalType } from 'src/types/Modal';
import { DateUtil, toMap, validatePermissionModule } from 'src/utils';
import { Document, DocumentResults } from '../../../types/Document';
import TableComponent, { KeyHeader } from '../../shared/TableComponent';
import { useTranslate } from 'sbx-crm-menu-ui';
import { Button } from '../../ui/button';
import NavbarBreadcrumbV2 from 'src/components/shared/NavbarBreadcrumbV2';
import { Permissions, PermissionType } from '../../../types/Permission';

const classNameHeader = 'px-6 py-3 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider';
export const DocumentListPageComponent = () => {

    const {
        dispatch,
        state: { loading: { [DataStates.DOCUMENT]: loadingDocuments }, session: {permissions_list} },
    } = useContext(AppAgentContext);

    const navigate = useNavigate();

    const [totalItems, setTotalItems] = useState<Document[]>([]);

    const { t: common } = useTranslate('common');

    const validateByPermissionType = ({document, permissionType}:{
        document: Document, permissionType: PermissionType
    }) => {
        const keys = validatePermissionModule({
            permissions_list,
            module: Permissions.SBX_STUDIO_DOCUMENT,
            types: [permissionType],
        });

        if (!document.id){
            return false
        }
        return keys.length === 0 || keys.includes(document.id);
    }

    const startingData = useCallback(async () => {
        dispatch(appActions.onLoading({ type: DataStates.DOCUMENT, status: State.LOADING }));
        const res = await getAllDocumentsService();
        if (res.success) {
            const res2 = await getLastResultsDocumentsService();
            if (res2.success) {
                const docs2 = toMap(res2.items, 'sbx_ai_doc_analysis');
                res.items = res.items.map(item => {
                    if (docs2[item.id]?.result) {
                        item.result = docs2[item.id].result;
                    }
                    return item;
                });
                const arrayItems = res.items.filter(document =>{
                    return validateByPermissionType({document, permissionType: PermissionType.READ})
                }).sort((a, b) => {
                    const date1 = new Date(a.updated_at) as Date;
                    const date2 = new Date(b.updated_at) as Date;
                    return date2.getTime() - date1.getTime();
                });
                setTotalItems(arrayItems);
                dispatch(appActions.onLoading({ type: DataStates.DOCUMENT, status: State.SUCCESS }));
            }
        } else {
            dispatch(appActions.onLoading({ type: DataStates.DOCUMENT, status: State.ERROR }));
        }
    }, []);

    useIgnoreRenderEffect(async () => {
        await startingData();
    }, [startingData]);

    if (loadingDocuments === State.LOADING) {
        return <LoadingComponent />;
    }

    return <div className="min-h-screen bg-gradient-to-br from-fuchsia-100 via-sky-100 to-violet-200 p-4 sm:p-8">
        <div className="max-w-6xl mx-auto">
            <NavbarBreadcrumbV2 routes={[
                    {
                        label: common('documents'),
                    }
                ]} 
            />
            {/* <nav className="text-sm mb-4">
                <a href="#" className="text-purple-600 hover:text-purple-800">{routes('home')}</a>
                <span className="mx-2 text-gray-500">&gt;</span>
                <span className="text-gray-700"> {common('documents')}</span>
            </nav> */}

            <h1 className="text-3xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                {common('documents')}
            </h1>

            <div className="bg-white bg-opacity-80 backdrop-blur-lg rounded-3xl shadow-xl p-6 mb-8">
                <TableComponent
                    searchClassName={'pl-10 pr-4 py-2 !border-purple-300 !focus:border-purple-500 focus:ring focus:!ring-purple-200 focus:ring-opacity-50 rounded-full'}
                    items={totalItems}
                    rowClassName={'border-purple-100 hover:bg-purple-50 transition-colors duration-200'}
                    topActions={
                        <div className="flex gap-2">
                            <Button
                                onClick={startingData}
                                className="w-full sm:w-auto primary-gradient-button hover:shadow-lg flex items-center justify-center"
                                // className={`flex btn btn-sm items-center`}
                                type="button"
                            >
                                <FontAwesomeIcon icon={faRotateLeft} />
                            </Button>
                            <Button
                                onClick={() => navigate('/document_detail_v2/create')}
                                className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold py-2 px-4 rounded-full transition-all duration-300 transform hover:scale-105"
                            >
                                <Plus size={20} className="mr-2" /> {common('new')}
                            </Button>
                        </div>}
                    pagePaginationClassName={(page, num) => {
                        return `w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${
                            page === num
                                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold'
                                : 'text-gray-600 hover:bg-purple-100'
                        }`;
                    }}
                    pagination
                    search
                    buttonPaginationClassName={'!bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full p-3 hover:from-purple-600 hover:to-pink-600 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-opacity-50'}
                    header={[
                        {
                            type: KeyHeader.KEY,
                            label: 'Nombre',
                            className: classNameHeader,
                            key: 'name',
                        },
                        {
                            type: KeyHeader.VALUE,
                            label: 'Creación',
                            className: classNameHeader,
                            toValue: (item) => {
                                if (item.created_at) {
                                    const utcDate = !item.created_at.includes('Z') ? item.created_at + 'Z' : item.created_at;
                                    return new DateUtil(new Date(utcDate), 'yyyy-MM-dd hh:mm:ss a').getDate();
                                }
                                return new DateUtil(new Date(), 'yyyy-MM-dd hh:mm:ss a').getDate();
                            },
                        },
                        {
                            type: KeyHeader.VALUE,
                            label: 'Actualización',
                            className: classNameHeader,
                            toValue: (item) => {
                                if (item.updated_at) {
                                    const utcDate = !item.updated_at.includes('Z') ? item.updated_at + 'Z' : item.updated_at;
                                    return new DateUtil(new Date(utcDate), 'yyyy-MM-dd hh:mm:ss a').getDate();
                                }
                                return new DateUtil(new Date(), 'yyyy-MM-dd hh:mm:ss a').getDate();
                            },
                        },
                        {
                            type: KeyHeader.VALUE,
                            label: 'Sentimientos',
                            className: classNameHeader,
                            toValue: (item) => {
                                const positive = item.getResults()?.sentiment_analysis_resume?.positive?.toFixed(5) || '';
                                const neutral = item.getResults()?.sentiment_analysis_resume?.neutral?.toFixed(5) || '';
                                const negative = item.getResults()?.sentiment_analysis_resume?.negative?.toFixed(5) || '';


                                return (
                                    <div className="w-[250px]">
                                        <ProgressBar data={[
                                            {
                                                value: positive,
                                                color: 'rgb(10, 222, 128)',
                                            },
                                            {
                                                value: neutral,
                                                color: 'rgb(251, 146, 60)',
                                            },
                                            {
                                                value: negative,
                                                color: 'rgb(248, 113, 113)',
                                            },
                                        ]} />
                                    </div>
                                );
                            },
                        },
                        {
                            type: KeyHeader.VALUE,
                            label: 'Estado',
                            className: classNameHeader,
                            toValue: (item) => item.getResults()?.status || '',
                        },
                    ]}
                    actionsLabelClassName={`px-6 py-3  text-xs font-semibold text-purple-700 uppercase tracking-wider`}
                    actions={[
                        {
                            label: 'Ver Resultados',
                            icon: <BarChart2 className="h-4 w-4" />,
                            className: 'h-8 w-8 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-600 flex items-center justify-center',
                            onClick: (item) => {
                                dispatch(appActions.openModal({
                                    type: ModalType.DOCUMENT_RESULTS,
                                    data: {
                                        title: '',
                                        onClose: () => dispatch(appActions.closeModal(ModalType.DOCUMENT_RESULTS)),
                                        data: {
                                            ...item.getResults(),
                                            source: item.getSource() ?? '',
                                        } as DocumentResults,
                                    },
                                }));
                            },
                        },
                        {
                            label: 'Editar',
                            icon: <Edit className="h-4 w-4" />,
                            visible: (item) => {
                                return validateByPermissionType({document: item, permissionType: PermissionType.EDIT})
                            },
                            className: 'h-8 w-8 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 flex items-center justify-center',
                            onClick: (item) => navigate(`/document_detail_v2/${item.id}`),
                        },
                        {
                            label: 'Eliminar',
                            icon: <Trash2 className="h-4 w-4" />,
                            visible: (item) => {
                                return validateByPermissionType({document: item, permissionType: PermissionType.DELETE})
                            },
                            className: `h-8 w-8 rounded-full bg-pink-100 hover:bg-pink-200 text-pink-600 flex items-center justify-center`,
                            onClick: () => {
                            },
                        },
                    ]}
                />
            </div>
        </div>
    </div>;


};
